/*
 * Loyalty Rewards & Achievements Page
 * BUDB - Gamified Experience
 * Developed by GRUDGE STUDIO
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
// Simple Progress component for now
const Progress = ({ value, className }: { value: number; className?: string }) => (
  <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
    <div 
      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

// Simple Tabs components for now
const Tabs = ({ defaultValue, className, children }: any) => {
  const [activeTab, setActiveTab] = React.useState(defaultValue);
  return (
    <div className={className} data-active-tab={activeTab}>
      {React.Children.map(children, child => 
        React.cloneElement(child, { activeTab, setActiveTab })
      )}  
    </div>
  );
};

const TabsList = ({ className, children, activeTab, setActiveTab }: any) => (
  <div className={`flex ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ value, className, children, activeTab, setActiveTab }: any) => (
  <button 
    className={`px-4 py-2 ${className} ${activeTab === value ? 'bg-white/20' : ''}`}
    onClick={() => setActiveTab(value)}
  >
    {children}
  </button>
);

const TabsContent = ({ value, className, children, activeTab }: any) => (
  activeTab === value ? <div className={className}>{children}</div> : null
);

// Simple cn utility for now
const cn = (...classes: (string | undefined | false)[]) => classes.filter(Boolean).join(' ');

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { 
  Crown, 
  Trophy, 
  Star, 
  Gift, 
  Coins, 
  Target, 
  Calendar,
  TrendingUp,
  Award,
  Zap,
  Clock,
  Users,
  Medal,
  Flame
} from 'lucide-react';

interface LoyaltyTier {
  name: string;
  minPoints: number;
  maxPoints: number;
  color: string;
  icon: string;
  dailyBonus: number;
  rewards: {
    dailyLogin: number;
    handPlayed: number;
    gameWon: number;
    tournamentEntry: number;
  };
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  gbuxReward: number;
  loyaltyPoints: number;
  rarity: string;
  unlocked?: boolean;
}

interface DailyMission {
  id: string;
  name: string;
  description: string;
  target: number;
  progress: number;
  gbuxReward: number;
  loyaltyPoints: number;
  icon: string;
  completed: boolean;
  claimed: boolean;
  canClaim: boolean;
}

interface LoyaltyProfile {
  currentTier: LoyaltyTier;
  nextTier: LoyaltyTier | null;
  pointsToNextTier: number;
  totalPoints: number;
  achievements: Achievement[];
  achievementCount: number;
  dailyMissions: DailyMission[];
  canClaimDailyBonus: boolean;
  rewardHistory: any[];
}

export default function LoyaltyRewards() {
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [userWallet] = useState('ErSGeWkLuKqmW2MNrcFWPsYryNPXDW224GmgNBf8ZT65');
  const [showAchievementDialog, setShowAchievementDialog] = useState(false);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch loyalty profile
  const { data: loyaltyProfile, isLoading } = useQuery<LoyaltyProfile>({
    queryKey: [`/api/loyalty/profile/${userWallet}`],
    enabled: !!userWallet,
    refetchInterval: 10000
  });

  // Fetch leaderboard
  const { data: leaderboard } = useQuery({
    queryKey: ['/api/loyalty/leaderboard'],
    refetchInterval: 30000
  });

  // Claim daily bonus mutation
  const claimDailyBonusMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/loyalty/daily-bonus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: userWallet })
      });
      if (!response.ok) throw new Error('Failed to claim daily bonus');
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Daily Bonus Claimed! 🎁",
        description: `Earned ${data.bonusAmount} GBUX + ${data.loyaltyReward?.pointsAwarded || 0} Loyalty Points`,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/loyalty/profile/${userWallet}`] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Claim Bonus",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Claim mission reward mutation
  const claimMissionMutation = useMutation({
    mutationFn: async (missionId: string) => {
      const response = await fetch(`/api/loyalty/claim-mission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: userWallet, missionId })
      });
      if (!response.ok) throw new Error('Failed to claim mission reward');
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Mission Completed! 🏆",
        description: `Earned ${data.gbuxReward} GBUX + ${data.loyaltyPoints} Loyalty Points`,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/loyalty/profile/${userWallet}`] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Claim Reward",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  if (isLoading || !loyaltyProfile) {
    return (
      <div className="poker-bg-page flex items-center justify-center">
        <div className="text-white text-xl">Loading Loyalty Rewards...</div>
      </div>
    );
  }

  const progressPercentage = loyaltyProfile.nextTier 
    ? ((loyaltyProfile.totalPoints - loyaltyProfile.currentTier.minPoints) / 
       (loyaltyProfile.nextTier.minPoints - loyaltyProfile.currentTier.minPoints)) * 100
    : 100;

  const getRarityColor = (rarity: string) => {
    const colors = {
      common: 'text-gray-400 border-gray-400',
      uncommon: 'text-green-400 border-green-400',
      rare: 'text-blue-400 border-blue-400',
      epic: 'text-purple-400 border-purple-400',
      legendary: 'text-yellow-400 border-yellow-400'
    };
    return colors[rarity as keyof typeof colors] || colors.common;
  };

  return (
    <div className="poker-bg-page">
      {/* Header */}
      <div className="bg-black/20 border-b border-yellow-600/30 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">Loyalty Rewards</h1>
              <p className="text-green-200">Earn GBUX tokens and climb the loyalty tiers</p>
            </div>
            <div className="text-right">
              <div className="text-yellow-400 text-2xl font-bold">
                {loyaltyProfile.totalPoints.toLocaleString()} Points
              </div>
              <Badge 
                variant="outline" 
                className={`border-2 ${loyaltyProfile.currentTier.color === '#CD7F32' ? 'border-orange-400 text-orange-400' :
                  loyaltyProfile.currentTier.color === '#C0C0C0' ? 'border-gray-300 text-gray-300' :
                  loyaltyProfile.currentTier.color === '#FFD700' ? 'border-yellow-400 text-yellow-400' :
                  loyaltyProfile.currentTier.color === '#E5E4E2' ? 'border-gray-200 text-gray-200' :
                  'border-purple-400 text-purple-400'}`}
              >
                {loyaltyProfile.currentTier.icon} {loyaltyProfile.currentTier.name}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-black/30">
            <TabsTrigger value="overview" className="text-white">Overview</TabsTrigger>
            <TabsTrigger value="missions" className="text-white">Daily Missions</TabsTrigger>
            <TabsTrigger value="achievements" className="text-white">Achievements</TabsTrigger>
            <TabsTrigger value="leaderboard" className="text-white">Leaderboard</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Current Tier & Progress */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-black/30 border-yellow-600/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Crown className="w-5 h-5 mr-2 text-yellow-400" />
                    Current Tier
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <div className="text-4xl mb-2">{loyaltyProfile.currentTier.icon}</div>
                    <div className="text-xl font-bold text-white">{loyaltyProfile.currentTier.name}</div>
                    <div className="text-green-400">Daily Bonus: {loyaltyProfile.currentTier.dailyBonus} GBUX</div>
                  </div>
                  
                  {loyaltyProfile.nextTier && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-white/70">
                        <span>Progress to {loyaltyProfile.nextTier.name}</span>
                        <span>{loyaltyProfile.pointsToNextTier} points needed</span>
                      </div>
                      <Progress value={progressPercentage} className="h-2" />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Daily Bonus */}
              <Card className="bg-black/30 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Gift className="w-5 h-5 mr-2 text-green-400" />
                    Daily Bonus
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <div className="text-3xl text-green-400 font-bold">
                    {loyaltyProfile.currentTier.dailyBonus} GBUX
                  </div>
                  <div className="text-white/70 text-sm">
                    + {loyaltyProfile.currentTier.rewards.dailyLogin} Loyalty Points
                  </div>
                  <Button
                    onClick={() => claimDailyBonusMutation.mutate()}
                    disabled={!loyaltyProfile.canClaimDailyBonus || claimDailyBonusMutation.isPending}
                    size="lg"
                    className={cn(
                      "w-full",
                      loyaltyProfile.canClaimDailyBonus 
                        ? "bg-gradient-to-r from-green-500 to-green-700 hover:from-green-400 hover:to-green-600"
                        : "bg-gray-600 cursor-not-allowed"
                    )}
                  >
                    {loyaltyProfile.canClaimDailyBonus ? (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Claim Daily Bonus
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        Already Claimed Today
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-black/30 border-blue-500/30">
                <CardContent className="p-4 text-center">
                  <Trophy className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-400">{loyaltyProfile.achievementCount}</div>
                  <div className="text-white/70 text-sm">Achievements</div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/30 border-purple-500/30">
                <CardContent className="p-4 text-center">
                  <Target className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-purple-400">
                    {loyaltyProfile.dailyMissions.filter(m => m.completed).length}
                  </div>
                  <div className="text-white/70 text-sm">Missions Complete</div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/30 border-yellow-500/30">
                <CardContent className="p-4 text-center">
                  <Coins className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-yellow-400">
                    {loyaltyProfile.rewardHistory.length}
                  </div>
                  <div className="text-white/70 text-sm">Rewards Earned</div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/30 border-green-500/30">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-400">
                    {loyaltyProfile.totalPoints.toLocaleString()}
                  </div>
                  <div className="text-white/70 text-sm">Total Points</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Daily Missions Tab */}
          <TabsContent value="missions" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {loyaltyProfile.dailyMissions.map((mission) => (
                <motion.div
                  key={mission.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className={cn(
                    "bg-black/40 border-2",
                    mission.completed ? "border-green-500/50" : "border-white/20"
                  )}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          <div className="text-2xl mr-3">{mission.icon}</div>
                          <div>
                            <h3 className="text-white font-semibold">{mission.name}</h3>
                            <p className="text-white/70 text-sm">{mission.description}</p>
                          </div>
                        </div>
                        {mission.completed && (
                          <Badge className="bg-green-500 text-white">
                            <Award className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between text-sm text-white/70">
                          <span>Progress</span>
                          <span>{mission.progress}/{mission.target}</span>
                        </div>
                        <Progress 
                          value={(mission.progress / mission.target) * 100} 
                          className="h-2" 
                        />

                        <div className="flex justify-between text-sm">
                          <span className="text-yellow-400">+{mission.gbuxReward} GBUX</span>
                          <span className="text-purple-400">+{mission.loyaltyPoints} Points</span>
                        </div>

                        {mission.canClaim && (
                          <Button
                            onClick={() => claimMissionMutation.mutate(mission.id)}
                            disabled={claimMissionMutation.isPending}
                            className="w-full bg-gradient-to-r from-green-500 to-green-700"
                          >
                            <Gift className="w-4 h-4 mr-2" />
                            Claim Reward
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {loyaltyProfile.achievements.map((achievement, index) => (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className={cn(
                      "bg-black/40 border-2 cursor-pointer hover:scale-105 transition-transform",
                      getRarityColor(achievement.rarity),
                      achievement.unlocked && "bg-gradient-to-br from-yellow-900/20 to-orange-900/20"
                    )}
                    onClick={() => {
                      setSelectedAchievement(achievement);
                      setShowAchievementDialog(true);
                    }}
                  >
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl mb-2">{achievement.icon}</div>
                      <h3 className="text-white font-semibold text-sm mb-1">{achievement.name}</h3>
                      <p className="text-white/60 text-xs mb-2">{achievement.description}</p>
                      <div className="space-y-1">
                        <div className="text-yellow-400 text-xs">+{achievement.gbuxReward} GBUX</div>
                        <div className="text-purple-400 text-xs">+{achievement.loyaltyPoints} Points</div>
                      </div>
                      {achievement.unlocked && (
                        <Badge className="mt-2 bg-green-500 text-white text-xs">
                          Unlocked
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-6">
            <Card className="bg-black/30 border-yellow-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Medal className="w-5 h-5 mr-2 text-yellow-400" />
                  Loyalty Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(leaderboard as any[])?.slice(0, 20).map((player: any, index: number) => (
                    <div 
                      key={player.walletAddress}
                      className="flex items-center justify-between p-3 bg-black/20 rounded border border-white/10"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                          index === 0 ? "bg-yellow-500 text-black" :
                          index === 1 ? "bg-gray-400 text-black" :
                          index === 2 ? "bg-orange-600 text-white" :
                          "bg-gray-600 text-white"
                        )}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-white font-semibold">{player.username}</div>
                          <div className="text-white/70 text-sm flex items-center">
                            {player.tierIcon} {player.tier}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-purple-400 font-semibold">
                          {player.loyaltyPoints.toLocaleString()} points
                        </div>
                        <div className="text-white/70 text-sm">
                          {player.achievements} achievements
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Achievement Details Dialog */}
      <Dialog open={showAchievementDialog} onOpenChange={setShowAchievementDialog}>
        <DialogContent className="bg-green-900 border-yellow-600/30">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center">
              {selectedAchievement?.icon} {selectedAchievement?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAchievement && (
            <div className="space-y-4">
              <p className="text-white/80">{selectedAchievement.description}</p>
              
              <div className="bg-black/30 p-4 rounded border border-white/20">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/70">GBUX Reward:</span>
                    <div className="text-yellow-400 font-semibold">
                      +{selectedAchievement.gbuxReward} GBUX
                    </div>
                  </div>
                  <div>
                    <span className="text-white/70">Loyalty Points:</span>
                    <div className="text-purple-400 font-semibold">
                      +{selectedAchievement.loyaltyPoints} Points
                    </div>
                  </div>
                  <div>
                    <span className="text-white/70">Rarity:</span>
                    <div className={cn("font-semibold capitalize", getRarityColor(selectedAchievement.rarity).split(' ')[0])}>
                      {selectedAchievement.rarity}
                    </div>
                  </div>
                  <div>
                    <span className="text-white/70">Status:</span>
                    <div className={selectedAchievement.unlocked ? "text-green-400" : "text-gray-400"}>
                      {selectedAchievement.unlocked ? "Unlocked" : "Locked"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}